// The keyboard map (B-418, APP_SHELL.md "Keyboard"): for a desktop's
// regulars, `g` then a letter goes to a place, and `?` shows the map.
//
// NEVER WHILE TYPING: a key in a field, with a modifier held, or with a sheet
// or a menu open is the field's or the sheet's, not a place to go. `/`
// (search) and `c` (chat) are their own components' (`findAnything`,
// `chatDrawer`), listed here so the map is whole.
//
// THE LISTS (M5, B-456): `j` and `k` move through the rows of whatever list
// the screen shows — any `.fsp-row`, My Team's table rows, or anything marked
// `data-kbd-row` — `Enter` opens the focused row as a tap on its name would
// (a player's card, a team), and on My Team `s` and `b` press a bench
// player's or a starter's badge, which starts the move a tap starts: the
// slots it can go to light up, and `Enter` on one puts him there. Nothing is
// moved by a key alone; the manager still says where.

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { leagueIdFromPath } from '../data/route';
import { Dialog, Stack, Text } from '../kit/ui';

/** How long after `g` its letter is waited for. */
const G_WAIT_MS = 1500;

/**
 * A `g` IS WAITING FOR ITS LETTER: `c` then is the Calendar, not chat, whose
 * own `c` (`chatDrawer`) asks this first, whichever of the two hears the key
 * first.
 */
let sequence = false;
export function placeSequencePending(): boolean {
  return sequence;
}

/** Where `g` and a letter go: inside a league, its places; anywhere, home. */
const PLACES: readonly {
  key: string;
  label: string;
  to: (league: string | null) => string | null;
}[] = [
  { key: 'h', label: 'My leagues', to: () => '/' },
  { key: 't', label: 'My Team', to: (l) => (l === null ? null : `/l/${l}`) },
  { key: 'd', label: 'Game Day', to: (l) => (l === null ? null : `/l/${l}/matchup`) },
  { key: 'p', label: 'Players', to: (l) => (l === null ? null : `/l/${l}/players`) },
  { key: 'l', label: 'League', to: (l) => (l === null ? null : `/l/${l}/league`) },
  { key: 'c', label: 'Calendar', to: (l) => (l === null ? null : `/l/${l}/calendar`) },
  { key: 's', label: 'Standings', to: (l) => (l === null ? null : `/l/${l}/standings`) },
  { key: 'n', label: 'News', to: (l) => (l === null ? null : `/l/${l}/news`) },
];

/** What counts as a row of a list. */
const ROWS = '.fsp-row, .fsp-sheet-row, [data-kbd-row]';

/**
 * What `Enter` presses in a focused row, first match first: a row's own
 * choice, its door, its name, a link. Never any other button, which could be
 * Drop.
 */
const OPENERS = [
  '[data-kbd-enter]',
  '.fsp-cover',
  '.fsp-name-btn, .fsp-name-link, .fsp-sheet-player',
  'a[href]',
];

/** The rows on screen, in order: in the page's main region, drawn, and not under a sheet. */
function rowsOnScreen(): HTMLElement[] {
  const main = document.querySelector('main');
  if (main === null) return [];
  return [...main.querySelectorAll<HTMLElement>(ROWS)].filter(
    (r) =>
      r.getClientRects().length > 0 &&
      // A list's column heads, drawn as a row, are not one to stop on.
      !r.hasAttribute('data-kbd-skip') &&
      r.closest('[aria-hidden="true"], [inert], [role="dialog"]') === null &&
      // A row inside a row (a list in a card) is the inner one's.
      r.parentElement?.closest(ROWS) == null,
  );
}

/** The row the keyboard is in, if any. */
function focusedRow(): HTMLElement | null {
  const at = document.activeElement;
  return at instanceof HTMLElement ? at.closest<HTMLElement>(ROWS) : null;
}

/** Move to the next row (`step` 1) or the one before (-1); with none focused, the first or last in view. */
function stepRows(step: 1 | -1): boolean {
  const rows = rowsOnScreen();
  if (rows.length === 0) return false;
  const here = focusedRow();
  const at = here === null ? -1 : rows.indexOf(here);
  let next: HTMLElement | undefined;
  if (at === -1) {
    const inView = rows.filter((r) => {
      const box = r.getBoundingClientRect();
      return box.bottom > 0 && box.top < window.innerHeight;
    });
    next = step === 1 ? (inView[0] ?? rows[0]) : (inView.at(-1) ?? rows.at(-1));
  } else {
    next = rows[Math.min(rows.length - 1, Math.max(0, at + step))];
  }
  if (next === undefined) return false;
  // A row is not a stop on Tab; it takes focus only when a key brings it there.
  if (!next.hasAttribute('tabindex')) next.setAttribute('tabindex', '-1');
  // Marked, so it scrolls clear of a sticky table head (`ui.css`).
  next.setAttribute('data-kbd-at', '');
  next.focus({ preventScroll: true });
  next.scrollIntoView({ block: 'nearest' });
  return true;
}

/** `Enter` on a focused row: its opener, else the row's own tap. */
function openRow(row: HTMLElement): boolean {
  for (const selector of OPENERS) {
    const el = row.querySelector<HTMLElement>(selector);
    if (el !== null) {
      el.click();
      return true;
    }
  }
  if (row.matches('.fsp-row-clickable, .fsp-sheet-row')) {
    row.click();
    return true;
  }
  return false;
}

/** A key the page should keep: typed into a field, chorded, or under a sheet or a menu. */
function notOurs(e: KeyboardEvent): boolean {
  if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return true;
  const t = e.target instanceof Element ? e.target : null;
  if (t !== null && t.closest('input, textarea, select, [contenteditable="true"]') !== null)
    return true;
  // OPEN ONES ONLY: a closed select keeps its listbox in the page, hidden,
  // and counting it silenced every key on Players. And not an explanation
  // shown while the pointer rests on a word (`Why`, `Term`): Mantine gives a
  // popover's dropdown the dialog role, and a mouse parked on a column head
  // left every key dead. One the keyboard is inside is still its own.
  const at = document.activeElement;
  return [...document.querySelectorAll('[role="dialog"], [role="menu"], [role="listbox"]')].some(
    (el) =>
      el.getClientRects().length > 0 && !(el.classList.contains('fsp-why-pop') && !el.contains(at)),
  );
}

export function KeyboardMap() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const league = leagueIdFromPath(pathname);
  const [open, setOpen] = useState(false);
  const pending = useRef<number | null>(null);

  useEffect(() => {
    const clear = () => {
      if (pending.current !== null) window.clearTimeout(pending.current);
      pending.current = null;
      sequence = false;
    };
    const onKey = (e: KeyboardEvent) => {
      if (notOurs(e)) {
        clear();
        return;
      }
      if (e.key === '?') {
        e.preventDefault();
        clear();
        setOpen(true);
        return;
      }
      if (pending.current !== null) {
        const place = PLACES.find((p) => p.key === e.key.toLowerCase());
        clear();
        const to = place?.to(league) ?? null;
        if (to !== null) {
          e.preventDefault();
          navigate(to);
        }
        return;
      }
      if (e.key === 'g') {
        pending.current = window.setTimeout(clear, G_WAIT_MS);
        sequence = true;
        return;
      }
      if (e.key === 'j' || e.key === 'k') {
        if (stepRows(e.key === 'j' ? 1 : -1)) e.preventDefault();
        return;
      }
      const row = focusedRow();
      if (row === null) return;
      // ENTER ON THE ROW ITSELF: a button inside it answers its own Enter.
      if (e.key === 'Enter' && document.activeElement === row) {
        if (openRow(row)) e.preventDefault();
        return;
      }
      if ((e.key === 's' || e.key === 'b') && row.dataset.kbdMove === e.key) {
        const badge = row.querySelector<HTMLElement>('.fsp-slot-btn');
        if (badge !== null) {
          e.preventDefault();
          badge.click();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      clear();
    };
  }, [league, navigate]);

  return (
    <Dialog opened={open} onClose={() => setOpen(false)} title="Keyboard">
      <Stack gap={2}>
        <dl className="fsp-keys">
          {PLACES.filter((p) => league !== null || p.to(null) !== null).map((p) => (
            <div key={p.key} className="fsp-keys-row">
              <dt>
                <kbd>g</kbd> <kbd>{p.key}</kbd>
              </dt>
              <dd>{p.label}</dd>
            </div>
          ))}
          <div className="fsp-keys-row">
            <dt>
              <kbd>/</kbd>
            </dt>
            <dd>Search</dd>
          </div>
          <div className="fsp-keys-row">
            <dt>
              <kbd>j</kbd> <kbd>k</kbd>
            </dt>
            <dd>Next and previous row of a list</dd>
          </div>
          <div className="fsp-keys-row">
            <dt>
              <kbd>Enter</kbd>
            </dt>
            <dd>Open the row: a player&rsquo;s card, a team</dd>
          </div>
          {league !== null ? (
            <div className="fsp-keys-row">
              <dt>
                <kbd>s</kbd> <kbd>b</kbd>
              </dt>
              <dd>
                In My Team&rsquo;s lineup, move a bench player in or a starter out: the slots that
                player can take light up, and <kbd>Enter</kbd> on one makes the move
              </dd>
            </div>
          ) : null}
          {league !== null ? (
            <div className="fsp-keys-row">
              <dt>
                <kbd>c</kbd>
              </dt>
              <dd>Chat</dd>
            </div>
          ) : null}
          <div className="fsp-keys-row">
            <dt>
              <kbd>Esc</kbd>
            </dt>
            <dd>Close what is on top</dd>
          </div>
          <div className="fsp-keys-row">
            <dt>
              <kbd>?</kbd>
            </dt>
            <dd>This map</dd>
          </div>
        </dl>
        <Text size="xs" tone="secondary" block>
          Not while typing in a field.
        </Text>
      </Stack>
    </Dialog>
  );
}
